/**
 * The command-survival table for the Data (Bases) merge.
 *
 * A three-clause rule, applied in order, resolves every word the merged CLI
 * inherited:
 *
 *   1. **Deleted** where WayAI's implementation already covers the word
 *      (identity, session, self-update, and report verbs).
 *   2. **Top level** iff the word names a Data ontology entity that WayAI does
 *      not also own as a concept.
 *   3. **Namespaced** otherwise — under `bases`, except where WayAI already owns
 *      a top-level command GROUP of that name, in which case the surface becomes
 *      a subgroup INSIDE WayAI's group.
 *
 * Flattening is never an option. This table is that rule's outcome, written once
 * so `registry.test.ts` can assert it rather than re-deriving it — the same
 * shape of guard the Data CLI used for its own operator-command filter.
 *
 * `shipped` is guarded in BOTH directions by `registry.test.ts`: a `true` entry
 * must be reachable, and a `false` entry must not be. So the flag cannot drift
 * ahead of the implementation or behind it, and the series that lands these
 * commands flips one flag per command it actually ships.
 */

export type Disposition =
  /** Clause 1 — WayAI's implementation wins; the word is not re-registered. */
  | 'deleted'
  /** Clause 2 — a Data ontology entity, registered at the CLI's top level. */
  | 'top-level'
  /** Clause 3 — namespaced under `bases`, or inside an existing WayAI group. */
  | 'namespaced'
  /** Neither namespaced nor deleted: one verb that routes by workspace subtree. */
  | 'routed';

export interface CommandSurvival {
  /** The word as the standalone Data CLI spelled it. */
  original: string;
  /** How the collision was resolved. */
  disposition: Disposition;
  /**
   * The surviving invocation, without the `wayai` binary name — e.g.
   * `'bases use'`. Null exactly when the word was deleted.
   */
  surviving: string | null;
  /** Which clause of the three-clause rule decided it (null for the routed verbs). */
  clause: 1 | 2 | 3 | null;
  /**
   * Whether the MERGE outcome this row describes is implemented today.
   *
   * For a `top-level` or `namespaced` row that means the command exists. For a
   * `routed` row it means something narrower: `wayai pull` and `wayai push` have
   * always existed, so what the flag tracks there is the SUBTREE ROUTING that
   * lets them target `bases/` — the row is about the merge, not the verb.
   */
  shipped: boolean;
  /** The contract's own reason, so the table explains itself at the call site. */
  reason: string;
}

/**
 * Every word the merge dispositioned: the twelve that collide lexically with a WayAI
 * command, the six that collide semantically under a different name, and the
 * ontology entities that survive at the top level.
 */
export const COMMAND_SURVIVAL: readonly CommandSurvival[] = [
  // --- Clause 1: deleted. One keyring login, one status, one CLI self-update, one report queue. ---
  {
    original: 'login',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: "One keyring login. WayAI's `login` and `~/.wayai/config.json` are the survivors.",
  },
  {
    original: 'logout',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: 'One session to clear.',
  },
  {
    original: 'whoami',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: 'One authenticated identity.',
  },
  {
    original: 'status',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: 'One auth/connectivity status.',
  },
  {
    original: 'init',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: 'One org binding per repo (`wayai-ws/wayai.yaml`), so one `init`.',
  },
  {
    original: 'update',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: 'One binary, one self-update.',
  },
  {
    original: 'report',
    disposition: 'deleted',
    surviving: null,
    clause: 1,
    shipped: true,
    reason: "One report queue. WayAI's `report` is the survivor.",
  },

  // --- Clause 2: top-level ontology entities WayAI owns no concept of. ---
  {
    original: 'bases',
    disposition: 'top-level',
    surviving: 'bases',
    clause: 2,
    shipped: true,
    reason: 'The ontology root entity. No WayAI command of this name.',
  },
  {
    original: 'records',
    disposition: 'top-level',
    surviving: 'records',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity. No WayAI command of this name.',
  },
  {
    original: 'record-types',
    disposition: 'top-level',
    surviving: 'record-types',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'relationships',
    disposition: 'top-level',
    surviving: 'relationships',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'relationship-types',
    disposition: 'top-level',
    surviving: 'relationship-types',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'query-relationships',
    disposition: 'top-level',
    surviving: 'query-relationships',
    clause: 2,
    shipped: true,
    reason: 'Ontology traversal over relationships.',
  },
  {
    original: 'files',
    disposition: 'top-level',
    surviving: 'files',
    clause: 2,
    shipped: true,
    reason:
      'Unambiguous: WayAI has no `files` command — hub-local resource files are reached through hub config-as-code.',
  },
  {
    original: 'file-types',
    disposition: 'top-level',
    surviving: 'file-types',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'attachments',
    disposition: 'top-level',
    surviving: 'attachments',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'toolsets',
    disposition: 'top-level',
    surviving: 'toolsets',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'actions',
    disposition: 'top-level',
    surviving: 'actions',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'triggers',
    disposition: 'top-level',
    surviving: 'triggers',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'inbound-webhooks',
    disposition: 'top-level',
    surviving: 'inbound-webhooks',
    clause: 2,
    shipped: true,
    reason: 'Ontology entity.',
  },
  {
    original: 'seed',
    disposition: 'top-level',
    surviving: 'seed',
    clause: 2,
    shipped: true,
    reason:
      'Ontology entity. `wayai status` vs `wayai seed lease status` differ by depth, not by word.',
  },

  // --- Clause 3: namespaced. ---
  {
    original: 'use',
    disposition: 'namespaced',
    surviving: 'bases use',
    clause: 3,
    shipped: true,
    reason:
      "Deprecated since the worktree scope merged into one set-valued file: `wayai use bases/<id>` covers this. Kept as an alias for its JSON output shape.",
  },
  {
    original: 'unbind',
    disposition: 'namespaced',
    surviving: 'bases unbind',
    clause: 3,
    shipped: true,
    reason:
      "Deprecated alongside `bases use`: `wayai unbind bases/<id>` covers this. It still clears only the bases axis of the worktree scope, never the hubs one.",
  },
  {
    original: 'bases promote',
    disposition: 'namespaced',
    surviving: 'bases promote',
    clause: 3,
    shipped: true,
    reason:
      'Already namespaced; kept verbatim. Distinct from `wayai publish` (hub production) — the two promote words stay distinct.',
  },
  {
    original: 'tokens',
    disposition: 'namespaced',
    surviving: 'bases tokens',
    clause: 3,
    shipped: true,
    reason: "Mints base tokens; WayAI's own token surface mints platform tokens.",
  },
  {
    // The organization vault this word named is retired: nothing resolves a value stored
    // there any more, so the surviving surface is `bases credentials` below — per-base
    // rows, which is what a `credential:<name>` reference actually reads. The row stays
    // (this table records how every original word was dispositioned, and the semantic
    // collision list is checked independently of it) with `shipped: false`, which
    // `registry.test.ts` enforces by asserting the invocation is NOT reachable.
    original: 'secrets',
    disposition: 'namespaced',
    surviving: 'bases secrets',
    clause: 3,
    shipped: false,
    reason: 'Retired with the organization vault — superseded by `bases credentials`.',
  },
  {
    original: 'credentials',
    disposition: 'namespaced',
    surviving: 'bases credentials',
    clause: 3,
    shipped: true,
    reason: "Per-base integration credentials; WayAI has connection and organization credentials.",
  },
  {
    original: 'sql',
    disposition: 'namespaced',
    surviving: 'bases sql',
    clause: 3,
    shipped: true,
    reason: 'WayAI already has tenant SQL over conversation analytics.',
  },
  {
    original: 'import',
    disposition: 'namespaced',
    surviving: 'bases import',
    clause: 3,
    shipped: true,
    reason: "WayAI has contact import (the organization's contact book).",
  },
  {
    original: 'batch',
    disposition: 'namespaced',
    surviving: 'bases batch',
    clause: 3,
    shipped: true,
    reason: 'Too generic to stand alone.',
  },
  {
    original: 'providers',
    disposition: 'namespaced',
    surviving: 'bases providers',
    clause: 3,
    shipped: true,
    reason: "WayAI's provider is a connector.",
  },
  {
    original: 'admin',
    disposition: 'namespaced',
    surviving: 'admin bases',
    clause: 3,
    shipped: false,
    reason:
      "Clause 3's group exception: `wayai admin` is an existing group with its own gating and subgroups, so the operator surface joins it as a peer rather than sitting under `bases`. UNSHIPPED pending a transport: the Data backend's operator routes live at `/admin/*`, a SIBLING of `/v1/*`, and the merged CLI reaches Data only through WayAI's `/api/data` proxy, which contains every forwarded path under `/v1` by construction (`rekor/upstream-url.ts`). The standalone CLI could call them because it held a second `api_url` pointed straight at the Data backend — the thing the merge deliberately removed. Shipping needs a proxy channel for operator routes plus platform-admin gating on it, which is a backend change, not a CLI one.",
  },

  // --- Neither: one verb each, routing by workspace subtree. ---
  {
    original: 'pull',
    disposition: 'routed',
    surviving: 'pull',
    clause: null,
    shipped: true,
    reason:
      'One verb, workspace-relative, routing by subtree: `hubs/` targets preview hubs, `bases/` targets preview bases. An invocation spanning both is refused, never merged.',
  },
  {
    original: 'push',
    disposition: 'routed',
    surviving: 'push',
    clause: null,
    shipped: true,
    reason: 'Same as `pull`.',
  },
] as const;

/**
 * The top-level words the Data surface claims. `index.ts` consults this only in
 * its dispatch `default:` branch, so a WayAI `case` always wins a tie — the
 * collision rule is enforced by the order of the switch, not by discipline.
 */
export const DATA_NAMESPACES: readonly string[] = COMMAND_SURVIVAL.filter(
  (c) => c.disposition === 'top-level' && c.shipped,
).map((c) => c.surviving as string);

/** True when `command` is a top-level word the Data surface owns. */
export function isDataNamespace(command: string | undefined): boolean {
  return command !== undefined && DATA_NAMESPACES.includes(command);
}
